import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useLang } from "@/lib/i18n";

// NOTE: draft legal text — the billing/refund/cancellation clauses reflect the
// current product policy, but the document should be reviewed by counsel before
// being treated as final/binding. Jurisdiction and company legal name are
// placeholders flagged for review.
const LAST_UPDATED = { pt: "7 de julho de 2026", en: "July 7, 2026", es: "7 de julio de 2026" };

const PAGE = {
  title: { pt: "Termos de Uso", en: "Terms of Use", es: "Términos de Uso" },
  intro: {
    pt: "Estes Termos de Uso regem o acesso e a utilização da plataforma Ratchet, operada pela Ratchet Labs. Ao criar uma conta ou usar o serviço, você concorda com estes termos.",
    en: "These Terms of Use govern access to and use of the Ratchet platform, operated by Ratchet Labs. By creating an account or using the service, you agree to these terms.",
    es: "Estos Términos de Uso rigen el acceso y el uso de la plataforma Ratchet, operada por Ratchet Labs. Al crear una cuenta o usar el servicio, aceptas estos términos.",
  },
  updatedLabel: { pt: "Última atualização", en: "Last updated", es: "Última actualización" },
};

const SECTIONS = [
  {
    title: { pt: "1. O serviço", en: "1. The service", es: "1. El servicio" },
    body: {
      pt: ["A Ratchet é uma plataforma SaaS de investigação automatizada de tickets de suporte, que se conecta às suas ferramentas e gera diagnósticos e relatórios. O serviço é fornecido “como está”, com melhorias contínuas."],
      en: ["Ratchet is a SaaS platform for automated support-ticket investigation that connects to your tools and produces diagnostics and reports. The service is provided “as is”, with continuous improvements."],
      es: ["Ratchet es una plataforma SaaS de investigación automatizada de tickets de soporte que se conecta a tus herramientas y genera diagnósticos e informes. El servicio se ofrece “tal cual”, con mejoras continuas."],
    },
  },
  {
    title: { pt: "2. Conta e responsabilidades", en: "2. Account and responsibilities", es: "2. Cuenta y responsabilidades" },
    body: {
      pt: ["Você é responsável por manter a confidencialidade das credenciais da sua conta, incluindo o segundo fator (TOTP), e por toda atividade realizada sob ela. Cada organização é isolada por identificador próprio, e as credenciais de integração são armazenadas de forma criptografada."],
      en: ["You are responsible for keeping your account credentials confidential, including the second factor (TOTP), and for all activity carried out under it. Each organization is isolated by its own identifier, and integration credentials are stored encrypted."],
      es: ["Eres responsable de mantener la confidencialidad de las credenciales de tu cuenta, incluido el segundo factor (TOTP), y de toda la actividad realizada con ella. Cada organización está aislada por su propio identificador, y las credenciales de integración se almacenan cifradas."],
    },
  },
  {
    title: { pt: "3. Planos, assinatura e cobrança", en: "3. Plans, subscription and billing", es: "3. Planes, suscripción y facturación" },
    body: {
      pt: [
        "Os planos pagos são contratados na forma de assinatura recorrente, nos ciclos mensal, semestral ou anual. O valor do ciclo escolhido é cobrado antecipadamente, no início de cada período, por meio de cartão.",
        "A assinatura é renovada automaticamente ao final de cada ciclo, pelo mesmo período e método de pagamento, até que seja cancelada. Descontos por ciclo (por exemplo, semestral ou anual) e cupons aplicáveis são refletidos no valor cobrado.",
      ],
      en: [
        "Paid plans are contracted as a recurring subscription, on monthly, semiannual or annual cycles. The amount for the chosen cycle is charged upfront, at the start of each period, by card.",
        "The subscription renews automatically at the end of each cycle, for the same period and payment method, until it is canceled. Per-cycle discounts (for example, semiannual or annual) and applicable coupons are reflected in the charged amount.",
      ],
      es: [
        "Los planes de pago se contratan como una suscripción recurrente, en ciclos mensual, semestral o anual. El importe del ciclo elegido se cobra por adelantado, al inicio de cada período, mediante tarjeta.",
        "La suscripción se renueva automáticamente al final de cada ciclo, por el mismo período y método de pago, hasta que se cancele. Los descuentos por ciclo (por ejemplo, semestral o anual) y los cupones aplicables se reflejan en el importe cobrado.",
      ],
    },
  },
  {
    title: { pt: "4. Cancelamento", en: "4. Cancellation", es: "4. Cancelación" },
    body: {
      pt: [
        "Você pode cancelar a assinatura a qualquer momento, pela própria plataforma. O cancelamento interrompe as renovações futuras: não haverá nova cobrança ao final do ciclo vigente.",
        "Após o cancelamento, o acesso ao plano contratado permanece ativo até o fim do período já pago. Ao término desse período, a organização retorna ao nível gratuito.",
      ],
      en: [
        "You may cancel the subscription at any time, from within the platform. Cancellation stops future renewals: no new charge is made at the end of the current cycle.",
        "After cancellation, access to the contracted plan remains active until the end of the already-paid period. At the end of that period, the organization returns to the free tier.",
      ],
      es: [
        "Puedes cancelar la suscripción en cualquier momento, desde la propia plataforma. La cancelación detiene las renovaciones futuras: no se realizará ningún nuevo cargo al final del ciclo vigente.",
        "Tras la cancelación, el acceso al plan contratado permanece activo hasta el final del período ya pagado. Al terminar ese período, la organización vuelve al nivel gratuito.",
      ],
    },
  },
  {
    highlight: true,
    title: { pt: "5. Política de reembolso", en: "5. Refund policy", es: "5. Política de reembolso" },
    body: {
      pt: [
        "O valor pago pelo período contratado — mensal, semestral ou anual — não é reembolsável, total ou parcialmente, pelo tempo restante em caso de cancelamento. O cancelamento é sempre permitido e apenas encerra as renovações futuras; o acesso é mantido até o fim do período já pago.",
        "Eventuais exceções (por exemplo, cobrança em duplicidade ou erro comprovado) são tratadas caso a caso pelo suporte.",
      ],
      en: [
        "The amount paid for the contracted period — monthly, semiannual or annual — is not refundable, in whole or in part, for the remaining time in the event of cancellation. Cancellation is always allowed and merely stops future renewals; access is kept until the end of the already-paid period.",
        "Any exceptions (for example, duplicate charges or a proven error) are handled case by case by support.",
      ],
      es: [
        "El importe pagado por el período contratado — mensual, semestral o anual — no es reembolsable, total ni parcialmente, por el tiempo restante en caso de cancelación. La cancelación siempre está permitida y solo detiene las renovaciones futuras; el acceso se mantiene hasta el final del período ya pagado.",
        "Las posibles excepciones (por ejemplo, un cargo duplicado o un error comprobado) se tratan caso por caso por el soporte.",
      ],
    },
  },
  {
    title: { pt: "6. Uso aceitável", en: "6. Acceptable use", es: "6. Uso aceptable" },
    body: {
      pt: ["Você concorda em não usar o serviço para fins ilícitos, em não tentar burlar limites de uso ou mecanismos de segurança, e em não acessar dados de outras organizações. O descumprimento pode levar à suspensão da conta."],
      en: ["You agree not to use the service for unlawful purposes, not to attempt to bypass usage limits or security mechanisms, and not to access other organizations' data. Violations may lead to account suspension."],
      es: ["Aceptas no utilizar el servicio con fines ilícitos, no intentar eludir los límites de uso o los mecanismos de seguridad, y no acceder a los datos de otras organizaciones. El incumplimiento puede conllevar la suspensión de la cuenta."],
    },
  },
  {
    title: { pt: "7. Dados e privacidade", en: "7. Data and privacy", es: "7. Datos y privacidad" },
    body: {
      pt: ["As credenciais de integração são criptografadas e isoladas por organização. Tratamos os dados conforme a legislação aplicável de proteção de dados. O uso do serviço também está sujeito à nossa Política de Privacidade."],
      en: ["Integration credentials are encrypted and isolated per organization. We process data in accordance with applicable data-protection law. Use of the service is also subject to our Privacy Policy."],
      es: ["Las credenciales de integración se cifran y se aíslan por organización. Tratamos los datos conforme a la legislación aplicable de protección de datos. El uso del servicio también está sujeto a nuestra Política de Privacidad."],
    },
  },
  {
    title: { pt: "8. Limitação de responsabilidade", en: "8. Limitation of liability", es: "8. Limitación de responsabilidad" },
    body: {
      pt: ["Na máxima extensão permitida em lei, a Ratchet Labs não se responsabiliza por danos indiretos, lucros cessantes ou perda de dados decorrentes do uso do serviço. Os relatórios gerados são apoio à decisão e não substituem a análise da sua equipe."],
      en: ["To the maximum extent permitted by law, Ratchet Labs is not liable for indirect damages, lost profits or data loss arising from use of the service. Generated reports are decision support and do not replace your team's own analysis."],
      es: ["En la máxima medida permitida por la ley, Ratchet Labs no se responsabiliza por daños indirectos, lucro cesante o pérdida de datos derivados del uso del servicio. Los informes generados son apoyo a la decisión y no sustituyen el análisis de tu equipo."],
    },
  },
  {
    title: { pt: "9. Alterações destes termos", en: "9. Changes to these terms", es: "9. Cambios en estos términos" },
    body: {
      pt: ["Podemos atualizar estes termos periodicamente. Alterações relevantes serão comunicadas por e-mail ou na plataforma. O uso continuado após a vigência das alterações representa concordância com a nova versão."],
      en: ["We may update these terms from time to time. Material changes will be communicated by email or in the platform. Continued use after the changes take effect constitutes acceptance of the new version."],
      es: ["Podemos actualizar estos términos periódicamente. Los cambios relevantes se comunicarán por correo electrónico o en la plataforma. El uso continuado tras la entrada en vigor de los cambios implica la aceptación de la nueva versión."],
    },
  },
  {
    title: { pt: "10. Lei aplicável e contato", en: "10. Governing law and contact", es: "10. Ley aplicable y contacto" },
    body: {
      pt: ["Estes termos são regidos pela legislação brasileira, no foro da comarca da sede da Ratchet Labs. Dúvidas sobre estes termos: owners@myratchet.com."],
      en: ["These terms are governed by Brazilian law, in the jurisdiction of Ratchet Labs' registered office. Questions about these terms: owners@myratchet.com."],
      es: ["Estos términos se rigen por la legislación brasileña, en la jurisdicción del domicilio social de Ratchet Labs. Consultas sobre estos términos: owners@myratchet.com."],
    },
  },
];

export default function Terms() {
  const { lang } = useLang();
  const L = (obj) => obj[lang] || obj.en || obj.pt;

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-20">
        <div className="overline mb-4">// legal</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">{L(PAGE.title)}</h1>
        <p className="mt-3 text-xs font-mono text-white/40">{L(PAGE.updatedLabel)}: {L(LAST_UPDATED)}</p>
        <p className="mt-6 text-white/60 leading-relaxed">{L(PAGE.intro)}</p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s, i) => (
            <div key={i} className={s.highlight ? "surface border-primary/40 p-6" : ""} data-testid={`terms-section-${i}`}>
              <h2 className="font-display font-bold text-xl tracking-tight mb-3">{L(s.title)}</h2>
              <div className="space-y-3">
                {L(s.body).map((para, j) => (
                  <p key={j} className="text-sm text-white/70 leading-relaxed">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
